// /finance/ar — 应收账款列表
// v1.31: 4 bucket (current / 0-30 / 31-60 / 61-90 / 90+) + 客户名 + 金额
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { CardSkeleton } from '../components/Skeleton';
import { Button } from '../components/ui';
import { type OrderAR, customerApi, financeApi } from '../lib/api';
import { downloadCsv } from '../lib/csv';
import { fmtDate, fmtMoney } from '../lib/format';

export const Route = createFileRoute('/finance/ar')({
  component: ARList,
});

// v1.31: 导出 for unit test (避免走完整 router)
export { ARList };

type BucketKey = 'all' | 'current' | '0-30' | '31-60' | '61-90' | '90+';

const BUCKET_LABELS: Record<BucketKey, string> = {
  all: '全部',
  current: '未到期',
  '0-30': '0-30 天',
  '31-60': '31-60 天',
  '61-90': '61-90 天',
  '90+': '90+ 天',
};

const BUCKET_COLOR: Record<string, string> = {
  current: 'bg-info/15 text-info',
  '0-30': 'bg-warn/15 text-warn',
  '31-60': 'bg-warn/20 text-warn',
  '61-90': 'bg-bad/15 text-bad',
  '90+': 'bg-bad/25 text-bad text-white',
  no_due_date: 'bg-surface-2 text-ink-3',
};

function ARList() {
  const [activeBucket, setActiveBucket] = useState<BucketKey>('all');
  // v1.32 P1-3: 排序 + 币种筛选
  const [sortBy, setSortBy] = useState<'amount' | 'dueDate' | 'customer' | 'aging'>('aging');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['finance', 'ar'],
    queryFn: () => financeApi.listAR(),
    refetchInterval: 60000,
  });

  const { data: customers } = useQuery({
    queryKey: ['customers', 'lookup'],
    queryFn: () => customerApi.list({ limit: 200 }),
  });

  const customerById = new Map((customers?.items ?? []).map((c) => [c.id, c]));

  const items = data?.items ?? [];
  // v1.32 P1-3: bucket 过滤 + 币种过滤
  let filtered =
    activeBucket === 'all' ? items : items.filter((it) => it.agingBucket === activeBucket);
  if (currencyFilter !== 'all') {
    filtered = filtered.filter((it) => it.currency === currencyFilter);
  }

  // v1.32 P1-3: 排序
  filtered = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'amount':
        return (a.outstandingCents - b.outstandingCents) * dir;
      case 'dueDate':
        return ((a.dueDate ?? 0) - (b.dueDate ?? 0)) * dir;
      case 'customer': {
        const aName = customerById.get(a.customerId)?.name ?? '';
        const bName = customerById.get(b.customerId)?.name ?? '';
        return aName.localeCompare(bName, 'zh') * dir;
      }
      case 'aging':
        return (a.daysOverdue - b.daysOverdue) * dir;
    }
  });

  const summary = data?.summary;
  const currency = summary?.currency ?? 'USD';

  // v1.32 P1-3: 币种去重 (让用户筛)
  const allCurrencies = Array.from(new Set(items.map((it) => it.currency))).sort();

  const counts: Record<BucketKey, number> = {
    all: items.length,
    current: items.filter((it) => it.agingBucket === 'current').length,
    '0-30': items.filter((it) => it.agingBucket === '0-30').length,
    '31-60': items.filter((it) => it.agingBucket === '31-60').length,
    '61-90': items.filter((it) => it.agingBucket === '61-90').length,
    '90+': items.filter((it) => it.agingBucket === '90+').length,
  };

  // v1.32 P1-3: toggle sort
  function toggleSort(field: typeof sortBy) {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  }
  function sortIcon(field: typeof sortBy) {
    if (sortBy !== field) return null;
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  if (error) {
    return (
      <div className="text-center text-bad text-sm py-8">
        加载失败: {(error as Error).message}
        <button
          type="button"
          onClick={() => refetch()}
          className="ml-3 text-accent hover:underline"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard
          label="应收总额"
          value={fmtMoney(summary?.totalOutstandingCents ?? 0, currency)}
          sub={`${summary?.totalOrders ?? 0} 笔未结清`}
          loading={isLoading}
        />
        <SummaryCard
          label="0-30 天"
          value={fmtMoney(summary?.buckets['0-30']?.outstandingCents ?? 0, currency)}
          sub={`${summary?.buckets['0-30']?.count ?? 0} 笔`}
          loading={isLoading}
          tone="warn"
        />
        <SummaryCard
          label="61-90 天"
          value={fmtMoney(summary?.buckets['61-90']?.outstandingCents ?? 0, currency)}
          sub={`${summary?.buckets['61-90']?.count ?? 0} 笔`}
          loading={isLoading}
          tone="bad"
        />
        <SummaryCard
          label="90+ 天"
          value={fmtMoney(summary?.buckets['90+']?.outstandingCents ?? 0, currency)}
          sub={`${summary?.buckets['90+']?.count ?? 0} 笔`}
          loading={isLoading}
          tone="bad-2"
        />
      </div>

      {/* Bucket tabs */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {(Object.keys(BUCKET_LABELS) as BucketKey[]).map((key) => {
            const active = activeBucket === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveBucket(key)}
                className={`
                  px-3 py-1.5 text-xs font-medium rounded transition-colors
                  ${
                    active
                      ? 'bg-accent text-white'
                      : 'bg-surface text-ink-2 border border-line hover:border-accent hover:text-accent'
                  }
                `}
              >
                {BUCKET_LABELS[key]}
                <span className={`ml-1.5 ${active ? 'opacity-80' : 'text-ink-3'}`}>
                  {counts[key]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          {/* v1.32 P1-3: 币种筛选 */}
          {allCurrencies.length > 1 && (
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
              className="text-xs px-2 py-1.5 border border-line rounded bg-surface text-ink-2"
              aria-label="币种筛选"
            >
              <option value="all">全部币种</option>
              {allCurrencies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconLeft="download"
            onClick={() => {
              const rows = filtered.map((it) => ({
                PO: it.poNumber || it.orderId.slice(0, 8),
                客户: customerById.get(it.customerId)?.name ?? '',
                未收金额_分: it.outstandingCents,
                已收_分: it.paidCents,
                总额_分: it.totalAmountCents,
                币种: it.currency,
                到期日: fmtDate(it.dueDate),
                账龄: it.agingBucket,
                超期天数: it.daysOverdue,
              }));
              const today = new Date().toISOString().slice(0, 10);
              downloadCsv(`ar-${today}.csv`, rows);
            }}
            disabled={filtered.length === 0}
          >
            导出 CSV
          </Button>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-1"
          >
            <Icon name="refresh-cw" size="xs" className={isFetching ? 'animate-spin' : ''} />
            {isFetching ? '刷新中' : '刷新'}
          </button>
        </div>
      </div>

      {/* v1.32 P1-3: 排序按钮组 */}
      <div className="flex items-center gap-2 mb-3 text-xs text-ink-3">
        <span>排序:</span>
        <button
          type="button"
          onClick={() => toggleSort('aging')}
          className={`px-2 py-0.5 rounded ${
            sortBy === 'aging' ? 'bg-accent/15 text-accent' : 'hover:text-ink-1'
          }`}
        >
          账龄{sortIcon('aging')}
        </button>
        <button
          type="button"
          onClick={() => toggleSort('amount')}
          className={`px-2 py-0.5 rounded ${
            sortBy === 'amount' ? 'bg-accent/15 text-accent' : 'hover:text-ink-1'
          }`}
        >
          金额{sortIcon('amount')}
        </button>
        <button
          type="button"
          onClick={() => toggleSort('dueDate')}
          className={`px-2 py-0.5 rounded ${
            sortBy === 'dueDate' ? 'bg-accent/15 text-accent' : 'hover:text-ink-1'
          }`}
        >
          到期日{sortIcon('dueDate')}
        </button>
        <button
          type="button"
          onClick={() => toggleSort('customer')}
          className={`px-2 py-0.5 rounded ${
            sortBy === 'customer' ? 'bg-accent/15 text-accent' : 'hover:text-ink-1'
          }`}
        >
          客户{sortIcon('customer')}
        </button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="circle-dollar-sign"
          title={activeBucket === 'all' ? '🎉 没有应收' : '该分桶下没有应收'}
          description="所有客户都已付清, 现金流健康"
        />
      ) : (
        <>
          {/* 桌面: 表格 */}
          <div className="hidden md:block bg-surface border border-line rounded overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-2 text-ink-2 text-xs">
                  <th className="text-left px-4 py-2 font-medium">订单</th>
                  <th className="text-left px-4 py-2 font-medium">客户</th>
                  <th className="text-right px-4 py-2 font-medium">未收金额</th>
                  <th className="text-right px-4 py-2 font-medium">已收 / 总额</th>
                  <th className="text-left px-4 py-2 font-medium">到期日</th>
                  <th className="text-left px-4 py-2 font-medium">状态</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <ARRow
                    key={it.orderId}
                    item={it}
                    customerName={customerById.get(it.customerId)?.name}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* 移动: 卡片 */}
          <div className="md:hidden space-y-2">
            {filtered.map((it) => (
              <ARCard
                key={it.orderId}
                item={it}
                customerName={customerById.get(it.customerId)?.name}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ARRow({ item, customerName }: { item: OrderAR; customerName?: string }) {
  // v1.32 P1-3: 红色高亮 overdue (>= 31 天)
  const isOverdue = item.daysOverdue >= 31;
  return (
    <tr className={`border-t border-line hover:bg-surface-2 ${isOverdue ? 'bg-bad/5' : ''}`}>
      <td className="px-4 py-3 text-sm font-medium">{item.poNumber || item.orderId.slice(0, 8)}</td>
      <td className="px-4 py-3 text-sm text-ink-2">{customerName ?? '—'}</td>
      <td className={`px-4 py-3 text-sm font-semibold text-right ${isOverdue ? 'text-bad' : ''}`}>
        {fmtMoney(item.outstandingCents, item.currency)}
      </td>
      <td className="px-4 py-3 text-xs text-ink-3 text-right">
        {fmtMoney(item.paidCents, item.currency)} / {fmtMoney(item.totalAmountCents, item.currency)}
      </td>
      <td className="px-4 py-3 text-xs text-ink-2">{fmtDate(item.dueDate)}</td>
      <td className="px-4 py-3">
        <BucketBadge bucket={item.agingBucket} daysOverdue={item.daysOverdue} />
      </td>
    </tr>
  );
}

function ARCard({ item, customerName }: { item: OrderAR; customerName?: string }) {
  return (
    <div className="bg-surface border border-line rounded p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {item.poNumber || item.orderId.slice(0, 8)}
          </div>
          <div className="text-xs text-ink-3 truncate">{customerName ?? '—'}</div>
        </div>
        <BucketBadge bucket={item.agingBucket} daysOverdue={item.daysOverdue} />
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-lg font-semibold">
            {fmtMoney(item.outstandingCents, item.currency)}
          </div>
          <div className="text-[10px] text-ink-3">
            已收 {fmtMoney(item.paidCents, item.currency)} /{' '}
            {fmtMoney(item.totalAmountCents, item.currency)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-ink-3">到期</div>
          <div className="text-xs">{fmtDate(item.dueDate)}</div>
        </div>
      </div>
    </div>
  );
}

function BucketBadge({ bucket, daysOverdue }: { bucket: string; daysOverdue: number }) {
  const label =
    bucket === 'no_due_date' ? '无到期日' : bucket === 'current' ? '未到期' : `${daysOverdue} 天`;
  return (
    <span
      className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${BUCKET_COLOR[bucket] ?? 'bg-surface-2 text-ink-3'}`}
    >
      {label}
    </span>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  loading,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub: string;
  loading: boolean;
  tone?: 'default' | 'warn' | 'bad' | 'bad-2';
}) {
  const toneClass =
    tone === 'warn'
      ? 'border-warn/30'
      : tone === 'bad'
        ? 'border-bad/30'
        : tone === 'bad-2'
          ? 'border-bad/50 bg-bad/5'
          : '';
  return (
    <div className={`bg-surface border border-line rounded p-3 ${toneClass}`}>
      <div className="text-[10px] text-ink-3 mb-1">{label}</div>
      {loading ? (
        <div className="h-6 w-20 bg-surface-2 rounded animate-pulse" />
      ) : (
        <div className="text-base sm:text-lg font-semibold">{value}</div>
      )}
      <div className="text-[10px] text-ink-3 mt-0.5">{sub}</div>
    </div>
  );
}
